import { CalendarClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeskId } from '@/hooks/useDeskId';
import { useScheduledMessages } from '@/modules/humanChat/hooks/useScheduledMessages';
import { selectSessionId } from '@/redux/session/selectors';

interface Props {
  /** Opens the schedule wizard (WhatsApp template → send time). */
  onBrowseTemplates: () => void;
  /** Toggles the pending scheduled-messages panel. */
  onToggleList: () => void;
  disabled?: boolean;
}

interface TriggerProps {
  ariaLabel: string;
  hasPending: boolean;
  disabled?: boolean;
  /** Set when the icon opens the wizard on its own — nothing is pending yet. */
  onClick?: () => void;
  /** Set instead of `onClick` when the icon opens the dropdown. */
  asMenuTrigger?: boolean;
}

/** Tooltip + calendar icon + pending dot, shared by both trigger shapes. */
const ScheduleTrigger = ({
  ariaLabel,
  hasPending,
  disabled,
  onClick,
  asMenuTrigger,
}: TriggerProps) => {
  const { t } = useTranslation();

  const button = (
    <Button
      type="button"
      variant="ghost-neutral"
      size="icon-sm"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      data-testid="schedule-send-button"
    >
      <CalendarClockIcon size={20} />
    </Button>
  );

  return (
    <InfoTooltip content={t('scheduled_messages.schedule_messages')} showArrow>
      <div className="relative">
        {asMenuTrigger ? <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger> : button}
        {hasPending && (
          <span
            data-testid="schedule-pending-dot"
            className="pointer-events-none absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-successful"
          />
        )}
      </div>
    </InfoTooltip>
  );
};

/**
 * Calendar-clock button between mic and Send, visible whenever scheduling is
 * available. With nothing pending it opens the schedule wizard straight away;
 * once messages are pending it shows a green dot and a two-item menu instead.
 */
const ScheduleSendButton = ({ onBrowseTemplates, onToggleList, disabled }: Props) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { isEnabled, pendingCount } = useScheduledMessages(deskId, sessionId);

  if (!isEnabled) {
    return null;
  }

  const hasPending = pendingCount > 0;
  // Announce the pending count so the trigger's purpose isn't just the green dot.
  const ariaLabel = hasPending
    ? t('scheduled_messages.schedule_messages_pending', { count: pendingCount })
    : t('scheduled_messages.schedule_messages');

  if (!hasPending) {
    return (
      <ScheduleTrigger
        ariaLabel={ariaLabel}
        hasPending={false}
        disabled={disabled}
        onClick={onBrowseTemplates}
      />
    );
  }

  return (
    <DropdownMenu>
      <ScheduleTrigger ariaLabel={ariaLabel} hasPending disabled={disabled} asMenuTrigger />

      <DropdownMenuContent align="end" side="top">
        <DropdownMenuItem onSelect={onToggleList}>
          {t('scheduled_messages.scheduled_messages_menu')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onBrowseTemplates}>
          {t('scheduled_messages.new_scheduled_message')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ScheduleSendButton;
